/**
 * Retour visuel du pilotage: le joystick virtuel (DOM) et le marqueur de
 * destination (three.js).
 *
 *   const vue = new JoystickVue(document.getElementById('interface')!)
 *   vue.montrer(x, y); vue.bouger(dx, dy, i); vue.cacher()
 *
 *   const marqueur = new MarqueurDestination(scene)
 *   marqueur.poser(x, y, z); marqueur.maj(dt); marqueur.effacer()
 */
import * as THREE from 'three'
import type { ITerrain } from '../../shared/types/world.ts'
import '../styles/joystick.css'

/** Nombre de secteurs d'un anneau. Chacun echantillonne le sol une fois. */
const SECTEURS = 40

/** Hauteur du marqueur au-dessus du sol, en metres. */
const GARDE = 0.06

/** Rayon de la course du pouce, en pixels. Doit suivre le seuil du Pointeur. */
const RAYON_PX = 60

export class JoystickVue {
  private readonly base: HTMLDivElement
  private readonly pouce: HTMLDivElement
  private visible = false

  constructor(parent: HTMLElement) {
    this.base = document.createElement('div')
    this.base.className = 'joystick'
    this.pouce = document.createElement('div')
    this.pouce.className = 'joystick__pouce'
    this.base.appendChild(this.pouce)
    parent.appendChild(this.base)
  }

  /** Place la base au point d'appui et lance l'animation d'apparition. */
  montrer(x: number, y: number): void {
    this.base.style.left = `${x}px`
    this.base.style.top = `${y}px`
    this.pouce.style.transform = 'translate(0px, 0px)'
    this.base.style.setProperty('--intensite', '0')
    if (!this.visible) {
      this.visible = true
      // Forcer un reflow pour rejouer l'animation d'apparition.
      void this.base.offsetWidth
      this.base.classList.add('joystick--visible')
    }
  }

  /** dx, dy en pixels depuis le point d'appui; intensite 0..1. */
  bouger(dx: number, dy: number, intensite: number): void {
    const longueur = Math.sqrt(dx * dx + dy * dy)
    let ox = dx
    let oy = dy
    if (longueur > RAYON_PX) {
      const k = RAYON_PX / longueur
      ox = dx * k
      oy = dy * k
    }
    this.pouce.style.transform = `translate(${ox.toFixed(1)}px, ${oy.toFixed(1)}px)`
    this.base.style.setProperty('--intensite', intensite.toFixed(2))
  }

  cacher(): void {
    if (!this.visible) return
    this.visible = false
    this.base.classList.remove('joystick--visible')
  }

  detruire(): void {
    this.base.remove()
  }
}

// ------------------------------------------------------- marqueur de destination

/** Duree d'une pulsation de l'onde, en secondes. */
const PERIODE_ONDE = 0.9

/**
 * Anneau pulsant pose a l'endroit tape. A effacer a l'arrivee.
 * Rendu en three.js pour coller au sol meme quand la camera pivote.
 */
/**
 * Bande d'anneau dans le plan XZ, hauteurs laissees a zero.
 *
 * On la fabrique a la main plutot qu'avec `RingGeometry`: il faut pouvoir
 * reecrire chaque sommet a la hauteur du sol, donc connaitre exactement leur
 * ordre. Deux sommets par secteur, interieur puis exterieur.
 */
function anneauPlat(): THREE.BufferGeometry {
  const sommets = (SECTEURS + 1) * 2
  const geo = new THREE.BufferGeometry()
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(sommets * 3), 3))
  const index: number[] = []
  for (let s = 0; s < SECTEURS; s++) {
    const a = s * 2
    const b = (s + 1) * 2
    index.push(a, a + 1, b + 1, a, b + 1, b)
  }
  geo.setIndex(index)
  return geo
}

export class MarqueurDestination {
  private readonly groupe: THREE.Group
  private readonly anneau: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>
  private readonly onde: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>
  private readonly scene: THREE.Scene
  private readonly terrain: ITerrain
  /** Centre du marqueur, pour echantillonner le sol autour de lui. */
  private cx = 0
  private cz = 0
  private cy = 0
  private t = 0
  private allume = false

  constructor(scene: THREE.Scene, terrain: ITerrain) {
    this.scene = scene
    this.terrain = terrain
    this.groupe = new THREE.Group()
    this.groupe.visible = false
    this.groupe.renderOrder = 3

    const geoAnneau = anneauPlat()
    const matAnneau = new THREE.MeshBasicMaterial({
      color: 0xffb43f,
      transparent: true,
      opacity: 0.9,
      depthWrite: false,
      side: THREE.DoubleSide,
    })
    this.anneau = new THREE.Mesh(geoAnneau, matAnneau)
    this.anneau.renderOrder = 3

    const geoOnde = anneauPlat()
    const matOnde = new THREE.MeshBasicMaterial({
      color: 0x4fd1c5,
      transparent: true,
      opacity: 0.6,
      depthWrite: false,
      side: THREE.DoubleSide,
    })
    this.onde = new THREE.Mesh(geoOnde, matOnde)
    this.onde.renderOrder = 3

    this.groupe.add(this.anneau)
    this.groupe.add(this.onde)
    this.scene.add(this.groupe)
  }

  /** Pose le marqueur au sol; y est la hauteur du terrain a cet endroit. */
  poser(x: number, y: number, z: number): void {
    this.cx = x
    this.cy = y
    this.cz = z
    // Un doigt au-dessus du sol pour eviter le combat de profondeur.
    this.groupe.position.set(x, y + GARDE, z)
    this.groupe.visible = true
    this.allume = true
    this.t = 0
    this.maj(0)
  }

  effacer(): void {
    this.allume = false
    this.groupe.visible = false
  }

  get visible(): boolean {
    return this.allume
  }

  maj(dt: number): void {
    if (!this.allume) return
    this.t += dt

    /*
     * LES ANNEAUX EPOUSENT LE SOL, ils ne sont plus poses a plat.
     *
     * C'etaient deux disques horizontaux places a la hauteur du CENTRE. Sur la
     * moindre pente, la moitie basse de l'anneau — large de presque un metre —
     * passait sous la terre et disparaissait: le marqueur se voyait ampute d'un
     * cote, ce qui est exactement ce qu'on ne veut pas d'un repere qui doit dire
     * « c'est ici ».
     *
     * On ne peut pas se contenter de mettre les mailles a l'echelle: un anneau
     * qui grandit passe sur un sol different. Chaque secteur va donc rechercher
     * la hauteur du sol a sa propre position, a chaque image. Quarante points par
     * anneau, uniquement quand le marqueur est visible: c'est negligeable devant
     * le reste de la scene, et ca marche sur une dune comme sur un escalier.
     */
    const battement = 1 + 0.08 * Math.sin(this.t * 6)
    this.epouserLeSol(this.anneau.geometry, 0.55 * battement, 0.8 * battement)
    this.anneau.material.opacity = 0.75 + 0.2 * Math.sin(this.t * 6)

    const phase = (this.t % PERIODE_ONDE) / PERIODE_ONDE
    const ech = 0.7 + phase * 1.5
    this.epouserLeSol(this.onde.geometry, 0.8 * ech, 0.95 * ech)
    this.onde.material.opacity = 0.55 * (1 - phase)
  }

  /**
   * Reecrit les sommets d'un anneau entre deux rayons, en collant au terrain.
   *
   * Les hauteurs sont relatives au groupe, lui-meme pose a la hauteur du centre:
   * un sommet vaut donc « hauteur du sol ici moins hauteur du sol au centre ».
   */
  private epouserLeSol(geo: THREE.BufferGeometry, rInterieur: number, rExterieur: number): void {
    const pos = geo.getAttribute('position') as THREE.BufferAttribute
    for (let s = 0; s <= SECTEURS; s++) {
      const a = (s / SECTEURS) * Math.PI * 2
      const ca = Math.cos(a)
      const sa = Math.sin(a)
      for (let k = 0; k < 2; k++) {
        const r = k === 0 ? rInterieur : rExterieur
        const x = ca * r
        const z = sa * r
        const y = this.terrain.hauteur(this.cx + x, this.cz + z) - this.cy
        pos.setXYZ(s * 2 + k, x, y, z)
      }
    }
    pos.needsUpdate = true
    geo.computeBoundingSphere()
  }

  detruire(): void {
    this.scene.remove(this.groupe)
    this.anneau.geometry.dispose()
    this.anneau.material.dispose()
    this.onde.geometry.dispose()
    this.onde.material.dispose()
  }
}
